import { SYNTHETIC_TRANSPORT_ERROR_CODE } from '../state/constants';
import type { SendMessageResponse } from './sendMessage.types';
import { SidekickHttpError } from './sidekickHttp';

const recordFromUnknown = (value: unknown): Record<string, unknown> | null => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

const requireString = (raw: Record<string, unknown>, key: string): string => {
  const value = raw[key];
  if (typeof value !== 'string' || value === '') {
    throw new SidekickHttpError(0, {
      code: SYNTHETIC_TRANSPORT_ERROR_CODE,
      message: `Sidekick SSE: SendMessageResponse missing or invalid ${key}`,
    });
  }
  return value;
};

/**
 * Runtime-validates the JSON response from `POST /messages` for both the
 * streaming ACK and sync response paths. Required: `messageId`,
 * `conversationId`, `acceptedAt`. Optional: `assistantMessage`, `traceId`,
 * `visualization`.
 */
export const parseSendMessageResponse = (
  value: unknown
): SendMessageResponse => {
  const root = recordFromUnknown(value);
  if (root === null) {
    throw new SidekickHttpError(0, {
      code: SYNTHETIC_TRANSPORT_ERROR_CODE,
      message: 'Sidekick SSE: SendMessageResponse is not an object',
    });
  }

  const messageId = requireString(root, 'messageId');
  const conversationId = requireString(root, 'conversationId');
  const acceptedAt = requireString(root, 'acceptedAt');

  const traceIdRaw = root.traceId;
  const traceId =
    typeof traceIdRaw === 'string' && traceIdRaw !== ''
      ? traceIdRaw
      : undefined;

  const assistantMessage = root.assistantMessage;
  const hasAssistant =
    assistantMessage !== undefined && assistantMessage !== null;
  const visualization = recordFromUnknown(root.visualization);

  return {
    messageId,
    conversationId,
    acceptedAt,
    ...(traceId !== undefined ? { traceId } : {}),
    ...(hasAssistant ? { assistantMessage } : {}),
    ...(visualization !== null
      ? { visualization: visualization as SendMessageResponse['visualization'] }
      : {}),
  };
};
